import { usePermissions } from '@my/directory-api/hooks';
import { useOrderDispatches } from '@my/tms-api/hooks';
import { MessageSquare } from 'lucide-react';

/**
 * Driver messages on this order — beside the Checkpoints icon, so a driver's
 * question is one click away instead of behind "send to driver". Opens the
 * Driver jobs panel on the conversations, unread first.
 *
 * <p>
 * The badge counts driver messages nobody in the office has read yet (amber);
 * with none unread the button stays, quiet, once there is any conversation.
 * Hidden until something has been sent to a driver — there is nobody to talk
 * to before that. Only with TMS Mobile.
 * </p>
 */
export function OrderMessagesButton({
  orderId,
  organisationId,
  onOpen,
}: {
  orderId: string | null;
  organisationId: string | null;
  onOpen: () => void;
}) {
  const { can } = usePermissions();
  const enabled = can('mobile') && !!orderId;
  const query = useOrderDispatches(enabled ? organisationId : null, enabled ? orderId : null);
  const jobs = query.data?.dispatches ?? [];
  if (!enabled || jobs.length === 0) return null;

  const unread = jobs.reduce((n, d) => n + d.unreadMessages, 0);
  const total = jobs.reduce((n, d) => n + d.messageCount, 0);

  return (
    <button
      type="button"
      onClick={onOpen}
      title={
        unread
          ? `${unread} new message${unread === 1 ? '' : 's'} from drivers`
          : total
            ? `Driver messages (${total})`
            : 'Message the driver'
      }
      aria-label="Driver messages"
      className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800"
    >
      <MessageSquare className="h-5 w-5" />
      {unread > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
          {unread}
        </span>
      )}
    </button>
  );
}
